/**
 * Each level's look (05 §4) from its two C64 colours: the block ramp runs from a shade of
 * the dark colour, through a blend, to the light colour, mixed in OKLab so the steps are even
 * in lightness. The back wall and the fog are near-black tints of the dark colour, so each
 * cave keeps the level's hue even in the dark.
 */

import c64 from '../../content/palettes/c64.json';
import type { LevelDef } from '../levels/types';
import { hexToRgb8, mixOklab, type Rgb8, rgb8ToHex } from './color';
import type { LevelLook } from './palette';
import { BLOCK_STYLES, type BlockStyle } from './stage/blockFaces';

const BLACK: Rgb8 = [8, 6, 14];

function c64Colour(index: number): Rgb8 {
  const hex = c64.colours[index];
  if (hex === undefined) throw new Error(`no C64 colour ${index}`);
  return hexToRgb8(hex);
}

export interface StyledLook extends LevelLook {
  style: BlockStyle;
}

export function levelLook(def: LevelDef): StyledLook {
  const light = c64Colour(def.colours[0]);
  const dark = c64Colour(def.colours[1]);
  const hex = (c: Rgb8): string => rgb8ToHex(c);
  const shade = (t: number): string => hex(mixOklab(dark, BLACK, t));
  return {
    // Consecutive levels never share a cut; 6 cuts and 21 colour pairs keep 100 levels apart.
    style: BLOCK_STYLES[(def.n - 1) % BLOCK_STYLES.length] ?? 'pillow',
    block: [shade(0.35), hex(dark), hex(mixOklab(dark, light, 0.5)), hex(light)],
    blockAlt: [shade(0.6), shade(0.35)],
    blockShadow: [0.45, 0.6, 0.72].flatMap((t) => [
      hex(mixOklab(light, BLACK, t)),
      hex(mixOklab(mixOklab(dark, light, 0.5), BLACK, t)),
    ]),
    backdrop: [shade(0.9), shade(0.85), shade(0.79)],
    fog: shade(0.95),
  };
}

/** The two looks of the game (08 §4): the remake's, and 1987's sixteen colours. */
export type Look = 'modern' | '1987';

/**
 * A level's look for the display option: in the 1987 look the C64's background was black, so the cave
 * wall and the fog are black (the glass bubbles are drawn over whatever is behind them, so this is done
 * in the palette, not with fog; as `?scene=lookdev&c64=1` does it).
 */
export function levelLookFor(def: LevelDef, look: Look): StyledLook {
  const own = levelLook(def);
  return look === '1987' ? { ...own, backdrop: ['#000000', '#000000', '#000000'], fog: '#000000' } : own;
}
